/**
 * Payroll backend: run monthly salaries from salary_chart.
 * Salaries are generated for the 1st of the given month; bonuses and other expenses are added separately.
 */

import { supabase } from './supabase';

/**
 * Run monthly salary generation for the given year and month.
 * Inserts one expense per employee from salary_chart (category Salary, date = 1st of month).
 * Idempotent: returns already_run if payroll was already run for that month.
 *
 * @param {number} year - e.g. 2025
 * @param {number} month - 1-12
 * @returns {Promise<{ ok: boolean, inserted?: number, reason?: string, message?: string }>}
 */
export async function runMonthlySalaries(year, month) {
  const p_year = Number(year);
  const p_month = Number(month);
  if (!Number.isInteger(p_year) || !Number.isInteger(p_month) || p_month < 1 || p_month > 12) {
    return { ok: false, reason: 'invalid_period', message: 'Invalid year or month.' };
  }

  const { data, error } = await supabase.rpc('run_monthly_salaries', {
    p_year: p_year,
    p_month: p_month,
  });

  if (error) {
    return { ok: false, reason: 'error', message: error.message };
  }

  if (data && typeof data === 'object' && 'ok' in data) {
    return data;
  }

  return { ok: false, reason: 'error', message: 'Unexpected response.' };
}

/**
 * Check if payroll has already been run for a given month.
 *
 * @param {number} year
 * @param {number} month - 1-12
 * @returns {Promise<boolean>}
 */
export async function isPayrollRunForMonth(year, month) {
  const { data, error } = await supabase
    .from('payroll_runs')
    .select('id')
    .eq('period_year', Number(year))
    .eq('period_month', Number(month))
    .maybeSingle();

  if (error) return false;
  return !!data;
}
